import React, { useCallback, useMemo, useState } from "react";
import { Badge, Button, Flex, Text, TextArea } from "@radix-ui/themes";
import { useQueryClient } from "react-query";
import { ChevronsUpDown, Check, X } from "lucide-react";

import { useSimulator } from "../../context/SimulatorWrapper";
import { useChat } from "../../context/ChatWrapper";
import { useTopicApi } from "@/v2/entities/topic/Topic.api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export const DescriptionEditTab = () => {
  const {
    topics,
    descriptionOverrides,
    setDescriptionOverride,
    removeDescriptionOverride,
    clearAllDescriptionOverrides,
    setActiveDescriptionTopicId: setActiveTopicId,
  } = useSimulator();
  const { messages, handleAddMessage } = useChat();
  const { updateTopic } = useTopicApi();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [open, setOpen] = useState(false);

  const hasMessages = messages.length > 0;

  const overrideEntries = useMemo(() => {
    return Object.entries(descriptionOverrides)
      .map(([id, content]) => {
        const topic = topics?.find((t) => t.id === Number(id));
        return { topicId: Number(id), content, topic };
      })
      .filter((e) => e.topic != null);
  }, [descriptionOverrides, topics]);

  const availableTopics = useMemo(() => topics ?? [], [topics]);

  const handleAddTopic = useCallback(
    (topicId: number) => {
      const topic = topics?.find((t) => t.id === topicId);
      if (!topic) return;
      if (!(topicId in descriptionOverrides)) {
        setDescriptionOverride(topicId, topic.description ?? "");
      }
      setActiveTopicId(topicId);
      setOpen(false);
    },
    [topics, descriptionOverrides, setDescriptionOverride, setActiveTopicId]
  );

  const handleContentChange = useCallback(
    (topicId: number, value: string) => {
      setDescriptionOverride(topicId, value);
    },
    [setDescriptionOverride]
  );

  const handleRemoveTopic = useCallback(
    (topicId: number) => {
      removeDescriptionOverride(topicId);
    },
    [removeDescriptionOverride]
  );

  const handleSaveAll = useCallback(async () => {
    setIsSaving(true);
    try {
      for (const entry of overrideEntries) {
        await updateTopic(entry.topicId, {
          id: entry.topicId,
          description: entry.content,
        });
      }
      clearAllDescriptionOverrides();
      setActiveTopicId(null);
      await queryClient.invalidateQueries(["topics"]);
    } finally {
      setIsSaving(false);
    }
  }, [overrideEntries, updateTopic, clearAllDescriptionOverrides, setActiveTopicId, queryClient]);

  const handleDiscardAll = useCallback(() => {
    clearAllDescriptionOverrides();
    setActiveTopicId(null);
  }, [clearAllDescriptionOverrides, setActiveTopicId]);

  const handleRerun = useCallback(async () => {
    setIsRerunning(true);
    try {
      const chatMessages = messages.map((msg) => ({
        content: msg.content,
        role: msg.role as "user" | "assistant",
      }));
      await handleAddMessage({ messages: chatMessages, descriptionOverrides });
    } finally {
      setIsRerunning(false);
    }
  }, [messages, handleAddMessage, descriptionOverrides]);

  return (
    <Flex direction="column" gap="3" p="3">
      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Add Topic
        </Text>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm",
                "placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              )}
            >
              <span className="text-muted-foreground">Search topics...</span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search topics..." />
              <CommandList>
                <CommandEmpty>No topics found.</CommandEmpty>
                <CommandGroup>
                  {availableTopics.map((topic) => (
                    <CommandItem
                      key={topic.id}
                      value={topic.topic}
                      onSelect={() => handleAddTopic(topic.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          topic.id in descriptionOverrides ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {topic.topic}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </Flex>

      {overrideEntries.map((entry) => (
        <Flex key={entry.topicId} direction="column" gap="2">
          <Flex align="center" justify="between">
            <Badge size="2" color="purple">
              {entry.topic?.topic}
            </Badge>
            <button
              onClick={() => handleRemoveTopic(entry.topicId)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </Flex>
          <TextArea
            value={entry.content}
            onChange={(e) => handleContentChange(entry.topicId, e.target.value)}
            placeholder="Enter topic description..."
            rows={3}
            style={{ width: "100%" }}
          />
        </Flex>
      ))}

      {overrideEntries.length > 0 && (
        <>
          <Flex gap="2" wrap="wrap">
            {hasMessages && (
              <Button
                size="2"
                variant="outline"
                onClick={handleRerun}
                disabled={isRerunning}
              >
                {isRerunning ? "Re-running..." : "Re-run"}
              </Button>
            )}
            <Button
              size="2"
              variant="solid"
              color="green"
              onClick={handleSaveAll}
              disabled={isSaving}
            >
              {isSaving
                ? "Saving..."
                : `Save${overrideEntries.length > 1 ? ` All (${overrideEntries.length})` : ""}`}
            </Button>
            <Button
              size="2"
              variant="soft"
              color="red"
              onClick={handleDiscardAll}
            >
              Discard All
            </Button>
          </Flex>
          {hasMessages && (
            <Text size="1" color="gray">
              Re-run reclassifies the full conversation using updated descriptions.
            </Text>
          )}
        </>
      )}
    </Flex>
  );
};
